import { getQuery } from '@/components/filter/helpers';
import { BaseApiClient } from '@/services/baseApiClient';
import { RequestError, type RequestOptions, uploadFile } from '@/utils/request';
import { ImportCustomEmissionFactorsFormField } from '@enmon/commons/lib/enmon';
import type {
  CreateCustomEmissionFactorArg,
  DeleteCustomEmissionFactorArg,
  GetCustomEmissionFactorArg,
  GetCustomEmissionFactorsArg,
  ImportCustomEmissionFactorArg,
  UpdateCustomEmissionFactorArg,
  CreateCustomEmissionFactorResult,
  DeleteCustomEmissionFactorResult,
  GetCustomEmissionFactorResult,
  GetCustomEmissionFactorsResult,
  ImportCustomEmissionFactorResult,
  UpdateCustomEmissionFactorResult,
} from './types';
import type { IEmissionFactorsApiClient } from './types/interface';

export class EmissionFactorsApiClient extends BaseApiClient implements IEmissionFactorsApiClient {
  constructor() {
    super('api/emission-factors');
  }

  public getOne(
    id: GetCustomEmissionFactorArg,
    signal: RequestOptions['signal'],
  ): Promise<GetCustomEmissionFactorResult> {
    return this.http.get(`${id}`, { signal });
  }

  public get(
    filter: GetCustomEmissionFactorsArg,
    signal: RequestOptions['signal'],
  ): Promise<GetCustomEmissionFactorsResult> {
    return this.http.get('', { query: getQuery(filter), signal });
  }

  public create(payload: CreateCustomEmissionFactorArg): Promise<CreateCustomEmissionFactorResult> {
    return this.http.post('', payload);
  }

  public update({ id, payload }: UpdateCustomEmissionFactorArg): Promise<UpdateCustomEmissionFactorResult> {
    return this.http.patch(`${id}`, payload);
  }

  public delete(id: DeleteCustomEmissionFactorArg): Promise<DeleteCustomEmissionFactorResult> {
    return this.http.delete(`${id}`);
  }

  public async import({
    importFile,
    attachments,
  }: ImportCustomEmissionFactorArg): Promise<ImportCustomEmissionFactorResult> {
    const formData = new FormData();
    formData.append(ImportCustomEmissionFactorsFormField.ImportFile, importFile);

    attachments.forEach(attachment => {
      formData.append(ImportCustomEmissionFactorsFormField.Attachments, attachment);
    });

    try {
      return await uploadFile(`${this.http.urlPrefix}/import`, formData, { signal: undefined });
    } catch (e) {
      if (e instanceof RequestError && e.response.status === 412) {
        return await e.response.json<ImportCustomEmissionFactorResult>();
      }
      throw e;
    }
  }
}
